import {type ChangeEvent, type FC, useCallback, useEffect, useRef, useState} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {UnthemedNavLink} from '@repo/shared-web/components/core/button';
import {notifyError} from '@repo/shared-web/lib/notification';

import {apiCall} from '@repo/website-frontend/lib/api';

interface NavBarProps {}

export const NavBar: FC = () => {
  const [location] = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const [newsgroupEnabled, setNewsgroupEnabled] = useState<boolean>();
  const [savingNewsgroupSetting, setSavingNewsgroupSetting] = useState(false);

  useEffect(() => {
    navRef.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView({block: 'nearest', inline: 'nearest'});
  }, [location]);

  useEffect(() => {
    let active = true;
    apiCall('POST /get-nzb-check-settings', {})
      .then(({newsgroupEnabled: enabled}) => {
        if (active) {
          setNewsgroupEnabled(enabled);
        }
      })
      .catch(error => {
        if (active) {
          notifyError(error);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const handleNewsgroupToggle = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    setSavingNewsgroupSetting(true);
    try {
      const {newsgroupEnabled: enabled} = await apiCall('POST /set-nzb-check-settings', {
        newsgroupEnabled: event.currentTarget.checked,
      });
      setNewsgroupEnabled(enabled);
    } catch (error) {
      notifyError(error);
    } finally {
      setSavingNewsgroupSetting(false);
    }
  }, []);

  return (
    <Wrapper>
      <NavLinks ref={navRef}>
        <StyledNavLink
          $active={location === '/nzbsu'}
          aria-current={location === '/nzbsu' ? 'page' : undefined}
          to="/nzbsu"
        >
          NZB.su
        </StyledNavLink>
        <StyledNavLink
          $active={location === '/browse'}
          aria-current={location === '/browse' ? 'page' : undefined}
          to="/browse"
        >
          Movies
        </StyledNavLink>
        <StyledNavLink
          $active={location === '/admin'}
          aria-current={location === '/admin' ? 'page' : undefined}
          to="/admin"
        >
          Admin
        </StyledNavLink>
        <StyledNavLink
          $active={location === '/koh-lanta'}
          aria-current={location === '/koh-lanta' ? 'page' : undefined}
          to="/koh-lanta"
        >
          Koh-Lanta
        </StyledNavLink>
      </NavLinks>
      <NewsgroupControl $disabled={newsgroupEnabled === undefined || savingNewsgroupSetting}>
        <span>Newsgroup</span>
        <SwitchInput
          type="checkbox"
          role="switch"
          aria-label="Allow automatic NZB checks to use the newsgroup account"
          aria-checked={newsgroupEnabled ?? false}
          checked={newsgroupEnabled ?? false}
          disabled={newsgroupEnabled === undefined || savingNewsgroupSetting}
          onChange={event => {
            void handleNewsgroupToggle(event);
          }}
        />
        <SwitchTrack aria-hidden="true">
          <SwitchThumb />
        </SwitchTrack>
        <SwitchState>
          {newsgroupEnabled ? 'On' : newsgroupEnabled === false ? 'Off' : '…'}
        </SwitchState>
      </NewsgroupControl>
    </Wrapper>
  );
};

NavBar.displayName = 'NavBar';

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  height: 64px;
  flex-shrink: 0;
  background: #1f242f;
  box-shadow: 0px 4px 10px -10px black;
`;

const NavLinks = styled.nav`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: calc(100% - 180px);
  height: 100%;
  min-width: 0;
  overflow-x: auto;
  @media (max-width: 600px) {
    justify-content: flex-start;
    gap: 4px;
    padding: 0 8px;
  }
`;

const StyledNavLink = styled(UnthemedNavLink)<{$active: boolean}>`
  flex-shrink: 0;
  color: #ffffffcc;
  padding: 8px 16px;
  border-radius: 4px;
  ${p => p.$active && 'background: #ffffff11;'}
  &:hover {
    ${p => !p.$active && 'background: #ffffff05;'}
  }
`;

const NewsgroupControl = styled.label<{$disabled: boolean}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 0 12px;
  white-space: nowrap;
  color: #ffffffcc;
  background: #1f242f;
  box-shadow: -8px 0 12px #1f242f;
  cursor: ${p => (p.$disabled ? 'default' : 'pointer')};
  opacity: ${p => (p.$disabled ? 0.6 : 1)};
  font-size: 14px;
`;

const SwitchInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;

  &:focus-visible + span {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
  }

  &:checked + span {
    background: #36a878;
  }

  &:checked + span > span {
    transform: translateX(14px);
  }
`;

const SwitchTrack = styled.span`
  display: inline-flex;
  align-items: center;
  width: 32px;
  height: 18px;
  padding: 2px;
  border-radius: 999px;
  background: #666d7a;
  transition: background 120ms ease;
`;

const SwitchThumb = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ffffff;
  transition: transform 120ms ease;
`;

const SwitchState = styled.span`
  min-width: 22px;
`;
